import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Users, DollarSign, ShoppingBag, Crown, Upload, Package, Activity, Image as ImageIcon, Settings2, X, Check, Plus, Trash2, Search, Tag, Bell, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { TestPurchaseCard, VaultEventsSection } from '@/components/AdminVault';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [userQuery, setUserQuery] = useState('');
  const [manageUserId, setManageUserId] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/users'),
      api.get('/admin/orders'),
      api.get('/products'),
      api.get('/admin/downloads-log'),
      api.get('/admin/discounts'),
    ])
      .then(([s, u, o, p, l, d]) => {
        setStats(s.data); setUsers(u.data); setOrders(o.data); setProducts(p.data); setLogs(l.data); setDiscounts(d.data);
      })
      .catch((e) => { console.warn('admin dashboard load failed', e); });
  }, [user]);

  // Auto-refresh new purchases every 20s (must be before early return)
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const t = setInterval(async () => {
      try {
        const [o, s] = await Promise.all([api.get('/admin/orders'), api.get('/admin/stats')]);
        setOrders(o.data); setStats(s.data);
      } catch (e) { console.warn('admin orders auto-refresh failed', e); }
    }, 20000);
    return () => clearInterval(t);
  }, [user]);

  if (loading) return <div className="text-center pt-40 text-white/50">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const upload = async (pid, file) => {
    if (!file) return;
    setUploading(pid);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/admin/upload/${pid}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded new file for ${pid}`);
      const p = await api.get('/products'); setProducts(p.data);
    } catch (e) {
      toast.error('Upload failed');
    } finally { setUploading(null); }
  };

  const uploadImage = async (pid, file) => {
    if (!file) return;
    setUploadingImage(pid);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/admin/upload-image/${pid}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Image updated for ${pid}`);
      const p = await api.get('/products'); setProducts(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image upload failed');
    } finally { setUploadingImage(null); }
  };

  const reloadUsers = async () => {
    const [u, s] = await Promise.all([api.get('/admin/users'), api.get('/admin/stats')]);
    setUsers(u.data); setStats(s.data);
  };

  const reloadDiscounts = async () => {
    const d = await api.get('/admin/discounts'); setDiscounts(d.data);
  };

  const paidOrders = orders.filter((o) => o.payment_status === 'paid');

  const q = userQuery.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.vip_code || '').toLowerCase().includes(q) ||
        u.user_id.toLowerCase().includes(q),
      )
    : users;

  return (
    <div className="bg-grain relative min-h-screen text-white">
      <Navbar />
      <div className="mx-auto max-w-7xl pt-32 pb-20 px-6 space-y-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-brand font-mono mb-3">Admin</p>
            <h1 className="font-display font-black text-5xl tracking-tighter">Control Room</h1>
          </div>
          <SyncAssetsButton />
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="admin-stats-grid">
            <StatBox icon={DollarSign} label="Revenue (USD)" value={`$${stats.revenue_aud.toFixed(2)}`} testid="stat-revenue" />
            <StatBox icon={ShoppingBag} label="Paid Orders" value={stats.total_orders_paid} testid="stat-orders" />
            <StatBox icon={Users} label="Customers" value={stats.total_users} testid="stat-users" />
            <StatBox icon={Crown} label="VIP Members" value={stats.total_vip} testid="stat-vip" />
          </div>
        )}

        {/* Live Purchases feed */}
        <section data-testid="purchase-feed-section">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-brand" />
            <h2 className="font-display font-bold text-2xl">Live purchases</h2>
            <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-brand font-mono">
              <span className="h-2 w-2 rounded-full bg-brand animate-pulse" /> auto-refresh
            </span>
            <span className="ml-auto text-xs text-white/50 font-mono">{paidOrders.length} paid</span>
          </div>
          <div className="glass rounded-2xl overflow-hidden">
            {paidOrders.length === 0 ? (
              <div className="text-center py-10 text-white/40 text-sm">No purchases yet.</div>
            ) : paidOrders.slice(0, 20).map((o) => (
              <div key={o.session_id} className="flex items-center gap-4 px-6 py-3 border-b border-white/5" data-testid={`purchase-row-${o.session_id}`}>
                <div className="h-9 w-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                  {o.kind === 'vip' ? <Crown className="h-4 w-4 text-brand" /> : <ShoppingBag className="h-4 w-4 text-brand" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">
                    <span className="font-medium">{o.user_email}</span>{' '}
                    <span className="text-white/50">purchased</span>{' '}
                    <span className="text-brand font-medium">{o.kind === 'vip' ? 'Lucky VIP Lifetime' : (o.product_name || o.product_id)}</span>
                  </div>
                  <div className="text-xs text-white/40 font-mono mt-0.5 flex items-center gap-3">
                    <span>{new Date(o.created_at).toLocaleString()}</span>
                    {o.discount_code && <span className="text-emerald-400">{o.discount_code}</span>}
                    {o.admin_granted && <span className="text-amber-400">Admin granted</span>}
                  </div>
                </div>
                <div className="font-mono text-white/80 shrink-0">${((o.amount_cents || 0) / 100).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Discount codes */}
        <TestPurchaseCard />
        <VaultEventsSection />

        <DiscountsSection
          discounts={discounts}
          products={products}
          onCreate={reloadDiscounts}
          onDelete={reloadDiscounts}
        />

        {/* Product upload */}
        <section>
          <div className="flex items-center gap-2 mb-4"><Package className="h-5 w-5 text-brand" /><h2 className="font-display font-bold text-2xl">Products &amp; downloads</h2></div>
          <p className="text-xs text-white/40 font-mono mb-4">Files &amp; images are stored in cloud object storage — they persist across deploys.</p>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/50">
              <div className="col-span-4">Product</div>
              <div className="col-span-2">Version</div>
              <div className="col-span-2">File</div>
              <div className="col-span-2">Replace file</div>
              <div className="col-span-2">Replace image</div>
            </div>
            {products.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center text-sm" data-testid={`admin-product-row-${p.id}`}>
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  {p.image_url && (
                    <img src={p.image_url.startsWith('http') || p.image_url.startsWith('/api') ? p.image_url : p.image_url}
                         alt="" className="h-12 w-9 rounded-md object-cover border border-white/10 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-white/40 font-normal font-sans truncate">{p.tagline}</div>
                  </div>
                </div>
                <div className="col-span-2 font-mono text-white/70">{p.version}</div>
                <div className="col-span-2">{p.has_file ? <span className="text-brand">Uploaded</span> : <span className="text-white/40">Missing</span>}</div>
                <div className="col-span-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                    <Upload className="h-3 w-3" />
                    <span>{uploading === p.id ? 'Uploading…' : 'Script file'}</span>
                    <input
                      data-testid={`admin-upload-${p.id}`}
                      type="file" className="hidden"
                      onChange={(e) => upload(p.id, e.target.files?.[0])}
                    />
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                    <ImageIcon className="h-3 w-3" />
                    <span>{uploadingImage === p.id ? 'Uploading…' : 'Image'}</span>
                    <input
                      data-testid={`admin-upload-image-${p.id}`}
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadImage(p.id, e.target.files?.[0])}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Orders */}
        <section>
          <h2 className="font-display font-bold text-2xl mb-4">Recent orders</h2>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="grid grid-cols-6 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/50">
              <div>Kind</div><div className="col-span-2">Email</div><div>Item</div><div>Amount</div><div>Status</div>
            </div>
            {orders.slice(0, 25).map((o) => (
              <div key={o.session_id} className="grid grid-cols-6 gap-4 px-6 py-3 border-b border-white/5 items-center text-sm">
                <div>{o.kind}</div>
                <div className="col-span-2 truncate">{o.user_email}</div>
                <div>{o.product_id || 'vip'}</div>
                <div className="font-mono">${((o.amount_cents || 0) / 100).toFixed(2)}</div>
                <div className={o.payment_status === 'paid' ? 'text-brand' : 'text-white/50'}>{o.payment_status}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Users */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-2xl">Customers</h2>
            <div className="relative">
              <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-user-search"
                type="text"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search email…"
                className="pl-9 pr-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:border-brand outline-none min-w-[220px]"
              />
            </div>
          </div>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/50">
              <div className="col-span-4">Email</div>
              <div className="col-span-1">Role</div>
              <div className="col-span-1">VIP</div>
              <div className="col-span-3">VIP Code</div>
              <div className="col-span-3 text-right">Manage</div>
            </div>
            {filteredUsers.slice(0, 100).map((u) => (
              <div key={u.user_id} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/5 items-center text-sm" data-testid={`user-row-${u.user_id}`}>
                <div className="col-span-4 truncate">{u.email}</div>
                <div className="col-span-1">{u.role}</div>
                <div className="col-span-1">{u.is_vip ? <span className="text-brand">Yes</span> : <span className="text-white/40">No</span>}</div>
                <div className="col-span-3 font-mono text-white/70 text-xs truncate">{u.vip_code || '—'}</div>
                <div className="col-span-3 text-right">
                  <button
                    data-testid={`manage-user-${u.user_id}`}
                    onClick={() => setManageUserId(u.user_id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-brand/10 hover:border-brand/40 text-xs text-white/80 transition-colors"
                  >
                    <Settings2 className="h-3 w-3" /> Manage
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Download logs */}
        <section>
          <div className="flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-brand" /><h2 className="font-display font-bold text-2xl">Download logs</h2></div>
          <div className="glass rounded-2xl p-4 text-xs font-mono max-h-64 overflow-auto no-scrollbar">
            {logs.length === 0 ? <div className="text-white/40 p-4">No downloads yet.</div> :
              logs.map((l, i) => (
                <div key={i} className="text-white/60 border-b border-white/5 py-1.5">
                  <span className="text-brand">{l.product_id}</span> · {l.user_id} · {new Date(l.at).toLocaleString()}
                </div>
              ))}
          </div>
        </section>
      </div>
      {manageUserId && (
        <ManageUserDialog
          userId={manageUserId}
          products={products}
          onClose={() => setManageUserId(null)}
          onChange={reloadUsers}
        />
      )}
      <Footer />
    </div>
  );
}

function ManageUserDialog({ userId, products, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/admin/users/${userId}`);
    setData(data);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const grant = async (pid) => {
    setBusy(`grant-${pid}`);
    try {
      await api.post(`/admin/users/${userId}/grant-product/${pid}`);
      toast.success('Access granted');
      await load(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Grant failed'); }
    finally { setBusy(null); }
  };
  const revoke = async (pid) => {
    setBusy(`revoke-${pid}`);
    try {
      await api.post(`/admin/users/${userId}/revoke-product/${pid}`);
      toast.success('Access revoked');
      await load(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); }
    finally { setBusy(null); }
  };
  const toggleVip = async () => {
    setBusy('vip');
    try {
      await api.post(`/admin/users/${userId}/toggle-vip`);
      toast.success('VIP status updated');
      await load(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'VIP toggle failed'); }
    finally { setBusy(null); }
  };
  const deleteUser = async () => {
    if (!window.confirm('Permanently delete this account and all its orders? This cannot be undone.')) return;
    setBusy('delete');
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Account deleted');
      onChange(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manage-user-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-strong rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-auto no-scrollbar">
        <div className="sticky top-0 z-10 glass-strong border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-xl">Manage account</h3>
          <button data-testid="close-manage-dialog" onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-6">
          {!data ? (
            <div className="text-center py-10 text-white/50">Loading…</div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                {data.user.picture && <img src={data.user.picture} alt="" className="h-12 w-12 rounded-full border border-white/10" />}
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{data.user.email}</div>
                  <div className="text-white/40 text-xs font-mono truncate">{data.user.user_id}</div>
                </div>
                <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${data.user.role === 'admin' ? 'bg-brand/20 text-brand' : 'bg-white/5 text-white/60'}`}>{data.user.role}</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-xs uppercase tracking-widest text-white/40 font-mono">VIP Status</div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={data.user.is_vip ? 'text-brand font-semibold' : 'text-white/50'}>{data.user.is_vip ? 'Active' : 'Inactive'}</span>
                    <button
                      data-testid="toggle-vip-btn"
                      onClick={toggleVip} disabled={busy === 'vip'}
                      className="text-xs px-3 py-1.5 rounded-full bg-brand/15 border border-brand/30 text-brand hover:bg-brand/25 transition-colors disabled:opacity-50"
                    >{busy === 'vip' ? '…' : (data.user.is_vip ? 'Revoke VIP' : 'Grant VIP')}</button>
                  </div>
                  {data.user.vip_code && (
                    <div className="mt-2 font-mono text-xs text-white/70 break-all">{data.user.vip_code}</div>
                  )}
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="text-xs uppercase tracking-widest text-white/40 font-mono">Orders</div>
                  <div className="mt-2 text-white font-display font-semibold text-2xl">{data.orders.length}</div>
                  <div className="text-white/40 text-xs">Owns {data.owned_product_ids.length} product(s)</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="user-downloads-box">
                  <div className="text-xs uppercase tracking-widest text-white/40 font-mono">Downloads</div>
                  <div className="mt-2 text-white font-display font-semibold text-2xl" data-testid="user-total-downloads">
                    {data.total_downloads || 0}
                  </div>
                  <div className="text-white/40 text-xs">
                    {data.last_download_at
                      ? `Last: ${new Date(data.last_download_at).toLocaleDateString()}`
                      : 'None yet'}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-white/40 font-mono mb-3">Product access</div>
                <div className="space-y-2">
                  {products.map((p) => {
                    const owns = data.owned_product_ids.includes(p.id);
                    const dlCount = (data.download_counts && data.download_counts[p.id]) || 0;
                    return (
                      <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`product-access-${p.id}`}>
                        {p.image_url && <img src={p.image_url} alt="" className="h-10 w-8 rounded object-cover shrink-0 border border-white/10" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-medium truncate">{p.name}</div>
                          <div className="text-white/40 text-xs font-mono truncate">{p.tagline}</div>
                        </div>
                        <span
                          className={`hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mr-2 ${dlCount > 0 ? 'text-brand' : 'text-white/30'}`}
                          data-testid={`downloads-${p.id}`}
                          title={`${dlCount} download${dlCount === 1 ? '' : 's'}`}
                        >
                          <Download className="h-3 w-3" /> {dlCount}
                        </span>
                        {owns ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400 mr-2"><Check className="h-3 w-3" /> Owns</span>
                            <button
                              data-testid={`revoke-${p.id}`}
                              onClick={() => revoke(p.id)} disabled={busy === `revoke-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                            ><Trash2 className="h-3 w-3" /> Revoke</button>
                          </>
                        ) : (
                          <button
                            data-testid={`grant-${p.id}`}
                            onClick={() => grant(p.id)} disabled={busy === `grant-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand/15 border border-brand/30 text-brand hover:bg-brand/25 transition-colors disabled:opacity-50"
                          ><Plus className="h-3 w-3" /> Grant</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-white/40 font-mono mb-3">Recent orders</div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] max-h-48 overflow-auto no-scrollbar">
                  {data.orders.length === 0 ? (
                    <div className="text-center py-6 text-white/40 text-sm">No orders</div>
                  ) : data.orders.map((o) => (
                    <div key={o.session_id} className="flex items-center gap-3 px-4 py-2 border-b border-white/5 text-xs">
                      <span className="text-white/60 uppercase tracking-widest">{o.kind}</span>
                      <span className="text-white flex-1 truncate">{o.product_name || o.product_id || '—'}</span>
                      <span className="font-mono text-white/60">${((o.amount_cents || 0) / 100).toFixed(2)}</span>
                      <span className={o.payment_status === 'paid' ? 'text-brand' : o.payment_status === 'revoked' ? 'text-red-400' : 'text-white/40'}>{o.payment_status}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                <button
                  data-testid="delete-user-btn"
                  onClick={deleteUser} disabled={busy === 'delete' || data.user.role === 'admin'}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title={data.user.role === 'admin' ? 'Cannot delete an admin' : ''}
                >
                  <Trash2 className="h-3 w-3" /> Delete account
                </button>
                <button onClick={onClose} className="text-sm px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">Close</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, testid }) {
  return (
    <div className="glass rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
        <Icon className="h-4 w-4 text-brand" />
      </div>
      <div className="mt-3 font-display font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

function SyncAssetsButton() {
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    if (!window.confirm('Re-download the latest product images and script files for OXIDE, CIPHER and KYROS and install them into this environment?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/sync-product-assets');
      const ok = (data.synced || []).filter((r) => r.image_ok && r.file_ok).length;
      const total = (data.synced || []).length;
      toast.success(`Synced ${ok}/${total} products`);
      // Nudge browsers to bust the image cache
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid="sync-product-assets-btn"
      onClick={sync}
      disabled={busy}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand/15 border border-brand/40 text-brand text-sm font-semibold hover:bg-brand/25 transition-colors disabled:opacity-50"
      title="Re-download OXIDE / CIPHER / KYROS images + files from the artifact CDN and install into this environment's storage."
    >
      <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Syncing…' : 'Sync product artwork'}
    </button>
  );
}

function DiscountsSection({ discounts, products, onCreate, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState('');
  const [type, setType] = useState('percent');
  const [value, setValue] = useState(20);
  const [appliesTo, setAppliesTo] = useState('all');
  const [maxUses, setMaxUses] = useState('');
  const [targets, setTargets] = useState([]);
  const targetOptions = [...products.map((p) => ({ id: p.id, label: p.name })), { id: 'vip', label: 'VIP Membership' }];
  const labelFor = (id) => targetOptions.find((t) => t.id === id)?.label || id;
  const toggleTarget = (id) => setTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        code, type, value: Number(value),
        applies_to: appliesTo,
        targets: appliesTo === 'specific' ? targets : [],
        max_uses: maxUses ? Number(maxUses) : null,
        starts_at: null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        active: true,
      };
      await api.post('/admin/discounts', payload);
      toast.success(`Discount ${code.toUpperCase()} created`);
      setCode(''); setValue(20); setMaxUses(''); setTargets([]); setEndsAt(''); setShowForm(false);
      onCreate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete discount code ${c}?`)) return;
    try {
      await api.delete(`/admin/discounts/${c}`);
      toast.success('Deleted');
      onDelete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/admin/discounts/${d.code}`, { active: !d.active });
      toast.success(d.active ? 'Deactivated' : 'Activated');
      onCreate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  return (
    <section data-testid="discounts-section">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="h-5 w-5 text-brand" />
        <h2 className="font-display font-bold text-2xl">Discount codes</h2>
        <button
          data-testid="new-discount-btn"
          onClick={() => setShowForm((v) => !v)}
          className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand/15 border border-brand/30 text-brand hover:bg-brand/25 transition-colors"
        >
          <Plus className="h-3 w-3" /> {showForm ? 'Cancel' : 'New code'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="glass rounded-2xl p-6 mb-4 grid gap-4 md:grid-cols-6" data-testid="discount-form">
          <label className="md:col-span-2 block">
            <span className="text-xs uppercase tracking-widest text-white/50">Code</span>
            <input
              data-testid="discount-code-input"
              required value={code} onChange={(e) => setCode(e.target.value)} maxLength={40}
              placeholder="LAUNCH20"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white uppercase font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50">Type</span>
            <select data-testid="discount-type-select" value={type} onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white">
              <option value="percent">% off</option>
              <option value="fixed">$ off (USD)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50">{type === 'percent' ? 'Percent off' : 'USD off'}</span>
            <input
              data-testid="discount-value-input"
              type="number" required min={1} max={type === 'percent' ? 99 : undefined} step={type === 'percent' ? 1 : 0.5}
              value={value} onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50">Applies to</span>
            <select data-testid="discount-applies-select" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white">
              <option value="all">Storewide</option>
              <option value="product">All scripts</option>
              <option value="vip">VIP only</option>
              <option value="specific">Specific items</option>
            </select>
          </label>
          {appliesTo === 'specific' && (
            <div className="md:col-span-6 flex flex-wrap gap-2" data-testid="discount-targets">
              {targetOptions.map((t) => (
                <button
                  key={t.id} type="button" data-testid={`discount-target-${t.id}`}
                  onClick={() => toggleTarget(t.id)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${targets.includes(t.id) ? 'bg-brand/20 border-brand text-white' : 'bg-white/[0.03] border-white/10 text-white/60 hover:text-white'}`}
                >{t.label}</button>
              ))}
            </div>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50">Max uses</span>
            <input
              type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white font-mono"
            />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-xs uppercase tracking-widest text-white/50">Ends at</span>
            <input
              type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 focus:border-brand outline-none text-white"
            />
          </label>
          <button
            data-testid="submit-discount-btn"
            type="submit" disabled={busy}
            className="md:col-span-2 self-end px-6 py-2.5 rounded-full bg-brand text-[#0a0a0a] font-semibold disabled:opacity-50 hover:shadow-[0_0_28px_rgb(var(--brand-rgb)/0.6)] transition-shadow"
          >{busy ? 'Creating…' : 'Create discount'}</button>
        </form>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/50">
          <div className="col-span-3">Code</div>
          <div className="col-span-2">Discount</div>
          <div className="col-span-2">Applies to</div>
          <div className="col-span-2">Usage</div>
          <div className="col-span-2">Expires</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {discounts.length === 0 ? (
          <div className="text-center py-10 text-white/40 text-sm">No discount codes yet.</div>
        ) : discounts.map((d) => (
          <div key={d.code} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/5 items-center text-sm" data-testid={`discount-row-${d.code}`}>
            <div className="col-span-3 font-mono text-white flex items-center gap-2">
              <span className={d.active ? '' : 'line-through opacity-50'}>{d.code}</span>
              {!d.active && <span className="text-[10px] uppercase tracking-widest text-white/40">Off</span>}
            </div>
            <div className="col-span-2 text-white">
              {d.type === 'percent' ? `${d.value}% off` : `$${d.value} USD off`}
            </div>
            <div className="col-span-2 text-white/70 text-xs" data-testid={`discount-applies-${d.code}`}>
              {d.applies_to === 'specific' ? (d.targets || []).map(labelFor).join(', ')
                : { all: 'Storewide', product: 'All scripts', vip: 'VIP only' }[d.applies_to || 'all']}
            </div>
            <div className="col-span-2 font-mono text-white/60 text-xs">
              {d.times_used || 0} / {d.max_uses ?? '∞'}
            </div>
            <div className="col-span-2 text-white/50 text-xs">
              {d.ends_at ? new Date(d.ends_at).toLocaleDateString() : 'Never'}
            </div>
            <div className="col-span-1 flex items-center justify-end gap-2">
              <button
                onClick={() => toggleActive(d)}
                className="text-xs text-white/60 hover:text-white transition-colors"
              >{d.active ? 'Pause' : 'Enable'}</button>
              <button
                data-testid={`delete-discount-${d.code}`}
                onClick={() => remove(d.code)}
                className="text-red-300/80 hover:text-red-300 transition-colors"
              ><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
